import type { FC, KeyboardEvent, ReactNode } from 'react'
import ReactCountryFlag from 'react-country-flag'
import type { Voice } from '@/features/voice/domain/models/voice.ts'
import PlayButton from '@/components/Sidebar/Scripts/PlayButton.tsx'

interface VoiceBoxProps extends Voice {
  onSelect?: () => void
  selected?: boolean
  children?: ReactNode
  isLoading?: boolean
}

const VoiceBox: FC<VoiceBoxProps> = ({
  id,
  picture,
  name,
  language,
  languageCode,
  accent,
  tags,
  audioSampleUrl,
  onSelect,
  selected,
  children,
  isLoading = false,
}) => {
  const isInteractive = typeof onSelect === 'function'
  const selectableProps = onSelect
    ? {
        onClick: onSelect,
        onKeyUp: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter') {
            onSelect()
          }
        },
        tabIndex: 0,
      }
    : {}

  const containerClasses = [
    'flex flex-col gap-1.5 rounded-lg bg-white/[7%] p-3 transition-all duration-300 w-full min-w-[280px]',
    'data-[interactive=true]:hover:bg-white/[14%] data-[interactive=true]:active:bg-white/[14%] data-[interactive=true]:focus:bg-white/[14%]',
    'data-[interactive=true]:active:shadow-[0_3px_0_0_theme(colors.blue.500)] data-[interactive=true]:focus:shadow-[0_3px_0_0_theme(colors.blue.500)] data-[interactive=true]:hover:shadow-[0_3px_0_0_theme(colors.blue.500)]',
    'data-[interactive=true]:cursor-pointer',
    'data-[selected=true]:shadow-[0_3px_0_0_theme(colors.blue.500)]',
  ].join(' ')

  return (
    <div
      className={`${containerClasses} ${isLoading ? 'opacity-50' : ''}`}
      data-selected={selected}
      data-interactive={isInteractive}
      {...selectableProps}
    >
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="relative h-10 w-10">
          <img
            src={picture}
            alt={name}
            className="h-full w-full rounded-full object-cover"
          />
          <span className="absolute -bottom-2.5 -right-1">
            <ReactCountryFlag
              countryCode={languageCode}
              svg
              style={{
                width: 16,
                height: 12,
              }}
              title={name}
            />
          </span>
        </div>
        <p className="mr-auto max-w-[160px] flex-1 truncate text-left text-sm font-medium capitalize text-white">
          {name}
        </p>
        <PlayButton soundId={id} audioUrl={audioSampleUrl} />
      </div>
      <div className="flex flex-row items-center gap-2 text-[14px] capitalize leading-[20px]">
        {language}
        <span className="h-1 w-1 rounded-full bg-zinc-400" />
        {accent}
      </div>
      <div className="flex flex-row items-center gap-2 text-xs capitalize leading-4 text-zinc-400">
        <span className="line-clamp-1">{tags.join(', ')}</span>
      </div>
      {children}
    </div>
  )
}

export default VoiceBox
